import { Building2, UserCheck, UserPlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { KioskStatePanel } from "../../../../../components/kiosk-state-panel";
import type { CheckInPatientLookupSearchState } from "./check-in-patient-lookup-model";

interface LookupResultActions {
  onReceptionHelp: () => void;
}

/**
 * Renders the outcome of a manual check-in lookup as one state panel: found
 * (confirm in the footer), not found (check spelling, reception, register),
 * not unique, or failed. The confirm action itself is the step's footer
 * action, so only one primary competes for attention (KC-67, KC-68).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <CheckInPatientLookupResultsPanel
 *   searchState={{ status: "not_found" }}
 *   onReceptionHelp={() => undefined}
 *   onRegisterPatient={() => undefined}
 * />
 * ```
 */
export function CheckInPatientLookupResultsPanel({
  searchState,
  onReceptionHelp,
  onRegisterPatient,
}: LookupResultActions & {
  searchState: CheckInPatientLookupSearchState;
  /** Present when the kiosk may register new patients. */
  onRegisterPatient?: () => void;
}) {
  const { t } = useTranslation();
  const receptionAction = {
    label: t("self-service.help-reception"),
    icon: Building2,
    onClick: onReceptionHelp,
    testId: "frontdesk-patient-lookup-reception-btn",
  };

  switch (searchState.status) {
    case "found":
      return (
        <KioskStatePanel
          tone="success"
          icon={<UserCheck />}
          title={t("self-service.check-in.patient-lookup.found-v2")}
          body={t("self-service.check-in.patient-lookup.found-confirm")}
          testId="frontdesk-patient-lookup-found"
        />
      );

    case "not_found":
      return onRegisterPatient ? (
        <KioskStatePanel
          tone="neutral"
          title={t("self-service.check-in.patient-lookup.not-found-title")}
          body={t("self-service.check-in.patient-lookup.not-found-check")}
          action={receptionAction}
          secondaryAction={{
            label: t("self-service.check-in.patient-lookup.create-new"),
            icon: UserPlus,
            onClick: onRegisterPatient,
            testId: "frontdesk-patient-lookup-create-btn",
          }}
          testId="frontdesk-patient-lookup-not-found"
        />
      ) : (
        <KioskStatePanel
          tone="neutral"
          icon={<Building2 />}
          title={t("self-service.check-in.patient-lookup.not-found-title")}
          body={t("self-service.check-in.patient-lookup.not-found-reception")}
          action={receptionAction}
          testId="frontdesk-patient-creation-reception-handoff"
        />
      );

    case "ambiguous":
      return searchState.hint === "firstname" ? null : (
        <KioskStatePanel
          tone="warning"
          title={t("self-service.check-in.patient-lookup.ambiguous-additional-v2")}
          action={receptionAction}
          testId="frontdesk-patient-lookup-ambiguous"
        />
      );

    case "error":
      return (
        <KioskStatePanel
          tone="error"
          title={t("self-service.check-in.patient-lookup.error-v2")}
          action={receptionAction}
          testId="frontdesk-patient-lookup-error"
        />
      );

    case "idle":
    case "searching":
      return null;
  }
}
